import React from "react";
import { CustomTypographyComponent } from "@cbex/ui/text";
import { useTheme } from "@cbex/utils/theme";
import {
  Stack,
  Chip,
  Paper,
  IconButton,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import dayjs from "dayjs";

import EventNoteIcon from "@mui/icons-material/EventNote";
import { BsPeopleFill, BsPersonLinesFill } from "react-icons/bs";
import { PiCertificateBold } from "react-icons/pi";
import {
  CbexPortalExamOverviewWithFilterOptionsRow,
  CbexPortalExamHistoryWithFilterOptionsRow,
  CertificateDocumentDataByMomentIdDocument,
  GetCbexPresenceDocumentDataDocument,
  ActivitiesByActivityIDsRow,
} from "@cbex/data-access";
import { downLoadCommunisPDF, mapCbexStatusColours } from "../utils";
import { useTranslations } from "next-intl";
import { CandidateOverviewModal } from "../modals";
import { useLazyQuery } from "@apollo/client";
import { enqueueSnackbar } from "notistack";

interface ActivityCardProps {
  inputData:
    | CbexPortalExamOverviewWithFilterOptionsRow
    | CbexPortalExamHistoryWithFilterOptionsRow;
  cardWidth?: number | string;
  isActivity?: boolean;
  isHistory?: boolean;
  isResultsCollected?: boolean;
  filterVar: any;
  onExamPressed?: (momentID: string) => void;
  refetch: any;
}

/**
 * @deprecated This isn't a plug and play UI, the extended one is {@link ActivityCardExtended}.
 * Buttons not added in the extended version yet.
 */
export const ActivityCard = (props: ActivityCardProps) => {
  const {
    inputData,
    cardWidth,
    isActivity = false,
    isHistory = false,
    isResultsCollected = false,
    onExamPressed,
  } = props;
  //@ts-ignore
  const [activityCardData, setActivityCardData] = React.useState<
    | CbexPortalExamOverviewWithFilterOptionsRow
    | CbexPortalExamHistoryWithFilterOptionsRow
  >();
  const theme = useTheme();

  const [candidateModal, setCandidateModal] = React.useState<boolean>(false);
  const t = useTranslations();
  React.useEffect(() => {
    if (inputData) {
      setActivityCardData(inputData);
    }
  }, [inputData]);

  const handleEditModalOpenState = (momentID: string) => {
    onExamPressed(momentID);
  };

  const handleCandidateModalOpenState = () => {
    setCandidateModal(!candidateModal);
  };

  const [getCertificateDocumentData] = useLazyQuery(
    CertificateDocumentDataByMomentIdDocument,
    {
      variables: {
        momentID: "",
      },
      fetchPolicy: "network-only",
      //@ts-ignore
    }
  );
  const [downloadState, setDownloadState] = React.useState<{
    isDownloading: boolean;
    progress: number;
    message: string;
    jobId?: string;
  }>({
    isDownloading: false,
    progress: 0,
    message: "",
  });

  const startDownload = async (pdfObject: any[], templateType: string) => {
    try {
      setDownloadState({
        isDownloading: true,
        progress: 0,
        message: "labels.startingZipCreation",
      });

      let fileName = `${activityCardData.moment.location.name}_${
        activityCardData.activity[0].name
      }_${new Date(activityCardData.startDate).toISOString().split("T")[0]}`;

      fileName = fileName.replace(/\s+/g, "_").toLowerCase();
      fileName = fileName + ".zip";

      // Start the download job
      const response = await fetch(
        `/api/compressDownload?template=${templateType}&fileName=${fileName}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            pdfObject,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to start download");
      }

      const { jobId } = await response.json();

      setDownloadState((prev) => ({
        ...prev,
        jobId,
        message: "labels.processing",
      }));

      // Small delay to ensure job is created before SSE connection
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Connect to SSE for progress updates
      const eventSource = new EventSource(`/api/compressDownloadSSE/${jobId}`);

      eventSource.addEventListener("status", (event: MessageEvent) => {
        const data = JSON.parse(event.data);
        setDownloadState((prev) => ({
          ...prev,
          progress: data.progress,
          message: data.message,
        }));
      });

      eventSource.addEventListener("complete", (event: MessageEvent) => {
        const data = JSON.parse(event.data);

        // Automatically download the file
        const link = document.createElement("a");
        link.href = data.downloadUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setDownloadState({
          isDownloading: false,
          progress: 100,
          message: "labels.completed",
        });

        enqueueSnackbar(t("feedback.success"), {
          variant: "success",
        });

        eventSource.close();
      });

      eventSource.addEventListener("error", (event: MessageEvent) => {
        let errorMessage = t("feedback.error");

        if (event.data) {
          try {
            const data = JSON.parse(event.data);
            errorMessage = data.message || data.error || errorMessage;
          } catch {
            // If parsing fails, use the raw data or default message
            errorMessage =
              typeof event.data === "string" ? event.data : errorMessage;
          }
        }

        setDownloadState({
          isDownloading: false,
          progress: 0,
          message: `${errorMessage}`,
        });

        enqueueSnackbar(errorMessage, {
          variant: "error",
        });

        eventSource.close();
      });

      eventSource.onerror = () => {
        const errorMsg = `${t("feedback.error")} ${t(
          "feedback.connectionError"
        )}`;
        setDownloadState({
          isDownloading: false,
          progress: 0,
          message: errorMsg,
        });

        enqueueSnackbar(errorMsg, {
          variant: "error",
        });

        eventSource.close();
      };
    } catch (error) {
      const errorMsg = `${t("feedback.error")} ${error.message}`;
      setDownloadState({
        isDownloading: false,
        progress: 0,
        message: errorMsg,
      });

      enqueueSnackbar(errorMsg, {
        variant: "error",
      });
    }
  };

  const handleDownloadClick = (momentID: string) => {
    getCertificateDocumentData({
      variables: {
        momentID: momentID,
      },
    })
      .then((val) => {
        const pdfObject = val.data.CertificateDocumentDataByMomentID;
        const templateType = pdfObject[0].activity.properties.template;
        startDownload(pdfObject, templateType);
      })
      .catch((error) => {
        const errorMsg = `${t("feedback.error")} ${error.message}`;
        setDownloadState({
          isDownloading: false,
          progress: 0,
          message: errorMsg,
        });

        enqueueSnackbar(errorMsg, {
          variant: "error",
        });
      });
  };

  const [getCBEXPresenceDocument] = useLazyQuery(
    GetCbexPresenceDocumentDataDocument,
    {
      variables: {
        momentID: "",
      },
      fetchPolicy: "network-only",
    }
  );

  const handleDownloadPresenceClick = (momentID: string) => {
    getCBEXPresenceDocument({
      variables: {
        momentID: momentID,
      },
    }).then((val) => {
      const error = downLoadCommunisPDF({
        pdfObject: val.data.CBEXExamDocumentData,
        templateType: "presencelist",
        downloadName: "presentielijst",
      });
    });
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        backgroundColor: theme.appColors.surfaceContainer,
        border: "none",
      }}
    >
      {activityCardData && (
        <Stack
          spacing={2}
          padding={2}
          width={cardWidth || "100%"}
          id="ActivityCard"
          direction={"row"}
        >
          <Stack padding={2} width="45%">
            <Stack width="100%" spacing={2}>
              <Stack
                spacing={{ md: 4, sm: 1 }}
                direction={{ sm: "column", md: "row" }}
              >
                <Stack
                  minWidth={{ md: 400, xs: 100 }}
                  spacing={{ md: 3, sm: 1 }}
                  direction={{ sm: "column", md: "row" }}
                >
                  <CustomTypographyComponent variant="h6">
                    {dayjs(activityCardData.moment.startDate).format(
                      "DD-MM-YYYY HH:mm"
                    )}
                  </CustomTypographyComponent>
                  <CustomTypographyComponent variant="h6">
                    {activityCardData.moment.location.city}
                  </CustomTypographyComponent>
                </Stack>
              </Stack>
              <Stack direction={"row"}>
                <CustomTypographyComponent mr={4} variant="body1" width={65}>
                  Locatie :
                </CustomTypographyComponent>
                <CustomTypographyComponent variant="body1">
                  {activityCardData.moment.location.name}
                  {", "}
                  {activityCardData.moment.location.street}{" "}
                  {activityCardData.moment.location.number}
                  {activityCardData.moment.location.suffix}
                </CustomTypographyComponent>
              </Stack>
              <Stack pt={3}>
                <Stack direction={"row"}>
                  <CustomTypographyComponent width={170} variant="body2">
                    Aantal kandidaten :
                  </CustomTypographyComponent>
                  <CustomTypographyComponent variant="body2">
                    {activityCardData.totalOrganizationCandidates}
                  </CustomTypographyComponent>
                </Stack>

                <Stack direction={"row"}>
                  <CustomTypographyComponent width={170} variant="body2">
                    Totaal aantal kandidaten :
                  </CustomTypographyComponent>
                  <CustomTypographyComponent variant="body2">
                    {activityCardData.totalCandidates}
                  </CustomTypographyComponent>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          <Stack padding={2} width={"45%"}>
            <Stack>
              {activityCardData &&
                activityCardData.activity &&
                activityCardData.activity.length > 0 &&
                activityCardData.activity.map(
                  (act: ActivitiesByActivityIDsRow) => (
                    <CustomTypographyComponent
                      key={act.activityID}
                      variant="subtitle1"
                    >
                      {act.name}
                    </CustomTypographyComponent>
                  )
                )}
            </Stack>
          </Stack>
          <Stack padding={2} width={200} justifyContent={"space-between"}>
            <Stack
              justifyContent={"flex-end"}
              width={"100%"}
              spacing={2}
              direction="column"
            >
              <Chip
                sx={{
                  backgroundColor: mapCbexStatusColours(
                    activityCardData.status,
                    theme
                  ),
                  color: theme.appColors.surfaceContainer,
                }}
                label={t(`value.${activityCardData.status}`)}
              />
              {activityCardData.moment.properties.publicExam && (
                <Chip
                  label={t(`common.publicExam`)}
                  sx={{ backgroundColor: theme.appColors.background }}
                />
              )}
            </Stack>
            <Stack alignItems={"flex-end"} width={"100%"} spacing={1}>
              {downloadState.isDownloading && (
                <Stack width="100%" spacing={1}>
                  <LinearProgress
                    variant="determinate"
                    value={downloadState.progress}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <CustomTypographyComponent
                    variant="caption"
                    textAlign="center"
                  >
                    {t(downloadState.message)}... ({downloadState.progress}%)
                  </CustomTypographyComponent>
                </Stack>
              )}

              <Stack spacing={2} direction="row" marginRight={"10px"}>
                {isActivity && activityCardData && !isResultsCollected && (
                  <IconButton
                    onClick={() =>
                      handleEditModalOpenState(activityCardData.momentID)
                    }
                    size="large"
                    sx={{ color: theme.palette.primary.main }}
                  >
                    <Tooltip title={t("common.viewExam")} placement="bottom">
                      <EventNoteIcon />
                    </Tooltip>
                  </IconButton>
                )}
                {(isHistory || isResultsCollected) && (
                  <IconButton onClick={handleCandidateModalOpenState}>
                    <Tooltip
                      title={t("labels.viewCandidate")}
                      placement="bottom"
                    >
                      <BsPeopleFill
                        size={24}
                        color={theme.palette.primary.main}
                      />
                    </Tooltip>
                  </IconButton>
                )}
                <IconButton
                  onClick={() =>
                    handleDownloadPresenceClick(
                      activityCardData.moment.momentID
                    )
                  }
                >
                  <Tooltip
                    title={t("labels.downloadPresenceList")}
                    placement="bottom"
                  >
                    <BsPersonLinesFill
                      size={24}
                      color={theme.palette.primary.main}
                      title={t("labels.downloadPresenceList")}
                    />
                  </Tooltip>
                </IconButton>
                {!isActivity &&
                  activityCardData.passedCount > 0 &&
                  activityCardData.activity[0].properties.template !==
                    undefined && (
                    <IconButton
                      disabled={downloadState.isDownloading}
                      onClick={() =>
                        handleDownloadClick(activityCardData.momentID)
                      }
                    >
                      <Tooltip
                        title={t("labels.downloadExamDetails")}
                        placement="bottom"
                      >
                        <PiCertificateBold
                          size={24}
                          color={
                            downloadState.isDownloading
                              ? theme.palette.grey[400]
                              : theme.palette.primary.main
                          }
                          title={t("labels.downloadExamDetails")}
                        />
                      </Tooltip>
                    </IconButton>
                  )}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      )}

      {candidateModal && (
        <CandidateOverviewModal
          city={activityCardData.moment.location.city}
          startDate={activityCardData.startDate}
          onModalOpen={candidateModal}
          momentID={activityCardData.moment.momentID}
          onModalClose={handleCandidateModalOpenState}
        />
      )}
    </Paper>
  );
};

export default ActivityCard;
